import { defineStore } from 'pinia'
import { ref } from 'vue'
import walletService, { type WalletTransaction } from '@/services/walletService'

const CACHE_TTL = 2 * 60 * 1000 // 2 minutes (balance changes more often)

export const useWalletStore = defineStore('wallet', () => {
  const balance = ref<number>(0)
  const recentTransactions = ref<WalletTransaction[]>([])
  const loading = ref(false)
  let balanceFetchedAt = 0
  let balancePromise: Promise<number> | null = null

  function isFresh(): boolean {
    return balanceFetchedAt > 0 && Date.now() - balanceFetchedAt < CACHE_TTL
  }

  async function fetchBalance(force = false): Promise<number> {
    if (!force && isFresh()) {
      return balance.value
    }
    if (balancePromise) return balancePromise

    balancePromise = (async () => {
      loading.value = true
      try {
        const res = await walletService.getBalance()
        if (res.data) {
          balance.value = res.data.balance || 0
          balanceFetchedAt = Date.now()
        }
        return balance.value
      } finally {
        loading.value = false
        balancePromise = null
      }
    })()

    return balancePromise
  }

  async function fetchRecentTransactions(): Promise<WalletTransaction[]> {
    try {
      const res = await walletService.getTransactions({ per_page: 10 })
      if (res.data) {
        recentTransactions.value = (res.data.data || []).filter(tx => tx.walletId === null)
      }
      return recentTransactions.value
    } catch {
      return recentTransactions.value
    }
  }

  function invalidateBalance() {
    balanceFetchedAt = 0
  }

  function $reset() {
    balance.value = 0
    recentTransactions.value = []
    balanceFetchedAt = 0
    balancePromise = null
    loading.value = false
  }

  return {
    balance,
    recentTransactions,
    loading,
    fetchBalance,
    fetchRecentTransactions,
    invalidateBalance,
    $reset,
  }
})
